import { useState, useEffect } from 'react';
import { hasProductAccess } from '@atlassian/jira-cross-join-nudges/src/services/permission-check/index.tsx';
import { fetchIsRequestAccessCapable } from '@atlassian/jira-cross-join-nudges/src/services/request-capabilities/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	INELIGIBLE_FOR_CONFLUENCE_ACCESS_ID,
	useIsEligibleForSpotlight,
	useUserSpotlightProperty,
} from './spotlight-check/index.tsx';

export const useCrossJoinTeamEligible = (
	projectId?: number,
): {
	isCrossJoinTeamEligible: boolean;
	canShowJoinTeamlozenge: boolean;
	canShowJoinTeamPulse: boolean;
} => {
	const [isCrossJoinTeamEligible, setIsCrossJoinTeamEligible] = useState(false);
	const [canShowJoinTeamlozenge, setCanShowJoinTeamLozenge] = useState(false);
	const [canShowJoinTeamPulse, setCanShowJoinTeamPulse] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const cloudId = useCloudId();
	const [checkIfSpotlightOrLozengeShown] = useIsEligibleForSpotlight();
	const [markAsNotEligible] = useUserSpotlightProperty(INELIGIBLE_FOR_CONFLUENCE_ACCESS_ID);
	useEffect(() => {
		if (!fg('cjn_project_pages_gate') || !projectId) {
			return;
		}

		const checkEligibility = async () => {
			let ineligibilityReason = '';
			let cohort = 'not-enrolled';
			try {
				const [canShowPulse, canShowlozenge, isIneligibleForAccess] =
					await checkIfSpotlightOrLozengeShown();

				if (isIneligibleForAccess) {
					ineligibilityReason = 'NOT_ELIGIBLE_FROM_LOCAL_STORE';
				} else if (canShowPulse === false) {
					cohort = 'variation'; // if spotlight is shown before, its safe to consider the user is in variation
					if (canShowlozenge) {
						ineligibilityReason = 'SPOTLIGHT_ELIGIBLE';
						setCanShowJoinTeamLozenge(canShowlozenge);
					} else {
						ineligibilityReason = 'ALREADY_SHOWN_SPOTLIGHT';
					}
				} else {
					const alreadyHasAccess = await hasProductAccess('confluence', cloudId);
					if (alreadyHasAccess) {
						ineligibilityReason = 'ALREADY_HAVE_ACCESS';
					} else {
						fireTrackAnalytics(
							createAnalyticsEvent({}),
							'crossJoinNudgeProjectPages capabilityCheck',
						);
						const canRequestAccess = await fetchIsRequestAccessCapable('confluence', cloudId);

						if (canRequestAccess) {
							cohort = expVal('cjn_project_pages_on_userid', 'cohort', 'not-enrolled');

							if (cohort === 'variation') {
								if (canShowPulse || canShowlozenge) {
									ineligibilityReason = 'SPOTLIGHT_ELIGIBLE';
									setCanShowJoinTeamPulse(canShowPulse);
									setCanShowJoinTeamLozenge(canShowlozenge);
								}
							} else {
								ineligibilityReason = 'NOT_ENROLLED_IN_VARIATION';
							}
						} else {
							ineligibilityReason = 'NOT_ELIGIBLE_FOR_REQUEST_ACCESS';
						}
					}
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: Error | any) {
				if (error instanceof Error && error.message?.includes('status code: 400')) {
					ineligibilityReason = 'NOT_ELIGIBLE_FOR_REQUEST_ACCESS';
				} else {
					ineligibilityReason = 'ERROR_EVALUATING_ELIGIBILITY';
				}
				fireErrorAnalytics({
					meta: {
						id: 'checkCJNEligibility',
						packageName: 'jiraNavigationAppsShortcutsSidebar',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
			}

			const eventDetail = {
				flagKey: 'cjn_project_pages_on_userid',
				cohort,
				ineligibilityReason,
			};
			if (
				['ALREADY_HAVE_ACCESS', 'NOT_ELIGIBLE_FOR_REQUEST_ACCESS'].includes(ineligibilityReason)
			) {
				markAsNotEligible();
			}

			if (cohort !== 'not-enrolled') {
				fireTrackAnalytics(createAnalyticsEvent({}), 'feature exposed', eventDetail);
			} else if (ineligibilityReason !== 'NOT_ELIGIBLE_FROM_LOCAL_STORE') {
				// we don't need to fire multiple 'feature excluded' event for same user if we find ineligibility in local storage,
				// this will reduce the event volume greatly
				fireOperationalAnalytics(createAnalyticsEvent({}), 'feature excluded', eventDetail);
			}

			const result = ineligibilityReason === 'SPOTLIGHT_ELIGIBLE';
			setIsCrossJoinTeamEligible(result);
		};

		checkEligibility();
	}, [projectId, cloudId, createAnalyticsEvent, checkIfSpotlightOrLozengeShown, markAsNotEligible]);

	return { isCrossJoinTeamEligible, canShowJoinTeamlozenge, canShowJoinTeamPulse };
};
